import utils from '@eitje/utils'
import {getGlobal} from '../helpers'
import {makeRange, date} from '@eitje/utils'
import ActiveRecord from './active_record/base'
import * as API from '../../requests'

class User extends ActiveRecord {
	static tableName = 'users'
	static defaultJoins = ['teams', 'userEmploymentTypes', 'salaries', 'contractHolders', 'workSchedules', 'environments']
	static sort = 'full_name'

	static invite({users, environment_id}) {
		return super.arbitrary('/invite', users, {extraParams: {env_id: environment_id}})
	}

	orgEnvIds = () => {
		return this.environment_ids.filter(id => getGlobal('myEnvIdsInOrg').includes(id))
	}

	// TODO discuss with Abel if we want this, since we need it
	update(data, rest = {}) {
		return super.update(data, {url: '/new_update', ...rest})
	}

	outOfService() {
		return !utils.exists(this.orgEnvIds())
	}

	inServiceOn(referenceDate) {
		return !referenceDate || !this.out_of_service_date || date(this.out_of_service_date).isAfter(referenceDate, 'day')
	}

	relevantEnvIds() {
		if (utils.exists(this.orgEnvIds())) return this.orgEnvIds()
		return this.del_env_ids
	}

	isDeleted(org = getGlobal('org')) {
		return !org.active_environment_ids.some(id => this.environment_ids.includes(id))
	}

	addToTeam(teamId) {
		return API.addAssoc('users', {team_ids: [teamId], id: this.id})
	}

	addToEnv(envId) {
		return API.addAssoc('users', {environment_ids: [envId], id: this.id})
	}

	deleteFromEnv(envId) {
		const delUrl = `environments/${envId}/users/${this.id}`
		return API.arbitrary('users', delUrl, {method: 'delete', user_id: this.id})
	}

	deleteFromTeam(teamId) {
		return API.removeAssoc('users', {team_ids: [teamId], id: this.id})
	}

	birthday() {
		return this.verjaardag && date(this.verjaardag)
	}

	hrIntegration() {
		const name = this.external_ids.find(id => id.hr)?.name
		if (name) return name.capitalize()
	}

	nextBirthday() {
		const birthday = this.birthday()
		if (!birthday) return

		const currentYear = date().year()
		const nextBday = birthday.clone().year(currentYear)

		if (nextBday.format() === date().format()) return date()

		if (nextBday.isBefore(date())) {
			nextBday.add(1, 'years')
		}

		return nextBday
	}

	address() {
		const city = this.stad
		const street = this.straat
		const number = this.huisnummer
		const postal_code = this.postcode

		let address = ''
		if (street) address += street
		if (street && number) address += ` ${number}`
		if (street && number && city) address += ','
		if (city) address += ` ${city}`
		if (city && postal_code) address += ` ${postal_code}`

		return address
	}

	previousBirthday() {
		const nextBday = this.nextBirthday()
		if (!nextBday) return
		return nextBday.clone().subtract(1, 'years')
	}

	age() {
		const birthday = this.birthday()
		if (!birthday) return

		const age = date().diff(birthday, 'years')

		// Don't show or use ages which are guaranteed to be false.
		if (age > 100) return

		return age
	}

	findContractPart(part, dateRange) {
		const parts = this[part]
		if (!parts || !dateRange) return null
		if (!dateRange.isDateRange) return parts.find(p => p.dateRange.contains(date(dateRange)))
		return parts.find(c => c.dateRange.overlaps(dateRange))
	}

	contractHolder = dateRange => this.findContractPart('contractHolders', dateRange)
	salary = dateRange => this.findContractPart('salaries', dateRange)
	userEmploymentType = dateRange => this.findContractPart('userEmploymentTypes', dateRange)
	workSchedule = dateRange => this.findContractPart('workSchedules', dateRange)

	currentContractHolder = () => this.findContractPart('contractHolders', todayDateRange())
	currentSalary = () => this.findContractPart('salaries', todayDateRange())
	currentUserEmploymentType = () => this.findContractPart('userEmploymentTypes', todayDateRange())
	currentWorkSchedule = () => this.findContractPart('workSchedules', todayDateRange())

	findNearestContractPart(part) {
		const parts = this[part]?.sortBy('start_date')
		if (!utils.exists(parts)) return

		const activePart = this.findContractPart(part, todayDateRange())
		if (activePart) return activePart

		const now = date()
		const futurePart = parts.find(part => date(part.start_date).isAfter(now))
		if (futurePart) return futurePart

		const pastPart = parts[parts.length - 1]
		if (pastPart) return pastPart
	}

	nearestContractHolder = () => this.findNearestContractPart('contractHolders')
	nearestSalary = () => this.findNearestContractPart('salaries')
	nearestUserEmploymentType = () => this.findNearestContractPart('userEmploymentTypes')
	nearestWorkSchedule = () => this.findNearestContractPart('workSchedules')

	hasSkillSet(skillSet) {
		return skillSet.skill_ids.length > 0 && skillSet.skill_ids.every(id => this.skill_ids.includes(id))
	}

	hasSkill(skill) {
		return this.skill_ids.includes(skill.id)
	}
}

const todayDateRange = () => {
	const today = date().format()
	return makeRange(today, today)
}

export default User
